import { Component, Input, OnInit, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { StateService } from '@uirouter/angular';
import { ScmService } from '../services/scm.service';
import { ScmChartService } from '../services/scm.chart.service';
import { UiService } from '../../../core/services/core.ui.service';
import { GfTitleService } from '../../../core/services/core.title.service';
import { UserService } from '../../../core/services/core.user.service';
import { UploadService } from '../../../core/services/core.upload.service';
import { HttpClient } from '@angular/common/http';
import * as _ from 'lodash';
import * as d3 from 'd3';

@Component({
  selector: 'scm-home',
  template: require('./scm.home.component.html')
})
export class ScmHomeComponent implements OnInit, OnChanges {
  @Input() project: any;
  @Input() scm: any;
  @ViewChild('nvd3', { static: false }) nvd3: any;

  forceReload = false;
  svnMessage = `<p>When defining your promotion model in SVN, GForge assumes you are using the convention of top level folders that includes tags/ branches/ and trunk/.</p><p>This means you should only add trunk or folders directly under branches.</p><p>Using folders in other (or deeper) locations will result in unpredictable behavior.</p><p>For more information, please read about <a style="font-weight: bold;" target="_blank" href="https://next.gforge.com/project/gforge5/wiki?pagename=Caveat%20with%20SVN%20and%20Promotion%20Models">SVN and Promotion Models</a> in our customer support wiki</p>`;
  scmAccessText: any = false;

  promotionModel: any[] = [];
  originalModel: any[] = [];
  modelDirty = false;
  newModel = '';

  search = {
    searching: false,
    name: '',
    searchItemsResult: []
  };

  user: any;
  protocol: string;

  maxUploadSize: number;

  canWrite: boolean;

  scmData: any;
  commitChartData: any;
  scmChartOptions: any;

  svnUpload: any;
  svnUploadSuccess: boolean;

  autoSubmitTimer: any;

  constructor(
    private stateService: StateService,
    private scmService: ScmService,
    private scmChartService: ScmChartService,
    private uiService: UiService,
    private userService: UserService,
    private uploadService: UploadService,
    private http: HttpClient,
    private titleService: GfTitleService
  ) {}

  ngOnInit(): void {
    this.protocol = window.location.protocol;
    this.scmChartOptions = this.scmChartService.commitChartOptions;

    this.titleService.setTitle(this.project.name, _.startCase(this.scm.scmType) + ' Info');
    this.user = this.userService.session.getUser();

    if (this.scm?.options?.accessText ?? null) {
      this.scmAccessText = {
        type: 'markdown',
        title: this.project.name,
        layout: 'media',
        content: this.scm.options.accessText
      };
    }

    this.maxUploadSize = this.uploadService.getMaxUploadSize('scm');

    if (this.scm.scmType === 'git' && !this.scm.defaultBranchName) {
      this.scm.defaultBranchName = 'main';
    }

    const level = this.getScmPermission();
    this.canWrite = this.userService.session.hasUser() && level > 0;

    this.getChartData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['originalModel']) {
      this.originalModel = changes['originalModel'].currentValue;
    }
    if (changes['promotionModel']) {
      this.promotionModel = changes['promotionModel'].currentValue;
    }
    if (!this.originalModel || !this.promotionModel) {
      this.modelDirty = false;
    } else {
      this.modelDirty = !_.isEqual(this.originalModel, this.promotionModel);
    }
  }

  checkAddItem(event: KeyboardEvent): void {
    const target = event.target as HTMLInputElement;
    if (event.key === 'Enter' && target.value !== '') {
      this.addItem(target.value);
      target.value = '';
      this.newModel = '';
    }
  }

  moveUp(idx: number): void {
    this.promotionModel = this.swap(idx, idx - 1, this.promotionModel);
  }

  moveDown(idx: number): void {
    this.promotionModel = this.swap(idx, idx + 1, this.promotionModel);
  }

  removeItem(idx: number): void {
    if (this.indexInArray(idx, this.promotionModel)) {
      this.promotionModel = this.promotionModel.filter((_, index) => idx !== index);
    }
  }

  addItem(newValue: string): void {
    const unixName = this.stateService.params.unixName;
    this.scmService.branch(unixName, newValue)
      .then(
        () => {},
        () => {
          this.uiService.message.showWarning(
            "This branch does not exist in the repository, It won't appear on the branch list until you truly create it.",
            'Warning'
          );
        }
      );

    const val = _.trim(newValue);
    if (val === '') return;
    this.promotionModel = [...new Set([...this.promotionModel, newValue])];
    this.search.name = '';
  }

  resetModel(): void {
    const unixName = this.stateService.params.unixName;
    this.scmService.promotionModel(unixName)
      .then(
        (response) => {
          this.promotionModel = _.reject(response.items, _.isEmpty);
          this.originalModel = this.promotionModel;
          this.uiService.message.showSuccess('Reset to the current promotion model.', 'Reset Model');
        },
        () => {}
      );
  }

  saveModel(): void {
    const unixName = this.stateService.params.unixName;
    const branches = _.reject(this.promotionModel, _.isEmpty).join(',');
    this.scmService.savePromotionModel(unixName, { branches })
      .then(
        () => {
          this.uiService.message.showSuccess('Updated the promotion model.', 'Success');
          this.originalModel = this.promotionModel;
        },
        () => {}
      );
  }

  searchBranch(searchValue: string): void {
    if (searchValue.length < 3) {
      return;
    }
    this.search.searching = true;
    const unixName = this.stateService.params.unixName;
    this.scmService.searchBranch(unixName, searchValue)
      .then(
        (response) => {
          this.search.searchItemsResult = response.items;
          this.search.searching = false;
        },
        () => {}
      );
  }

  addBranchAutoSubmit(searchValue: string): void {
    if (this.autoSubmitTimer) {
      clearTimeout(this.autoSubmitTimer);
    }
    this.autoSubmitTimer = setTimeout(() => {
      this.searchBranch(searchValue);
    }, 750);
  }

  addBranch(branch: string): void {
    this.search.name = branch;
    this.search.searching = false;
    this.search.searchItemsResult = [];
  }

  private getScmPermission(): number {
    const type = this.scm.scmType;
    if (_.get(this.project, 'rel.permissions.admin', 0)) return 99;
    return _.get(this.project, `rel.permissions.scm${type}`, 0);
  }

  private reCheckEmptyRepo(): void {
    const unixName = this.stateService.params.unixName;
    this.scmService.fetch(unixName, 'scm')
      .then(
        (scm) => {
          if (_.get(scm, 'options.isEmpty', false) === false) {
            this.stateService.reload();
          } else {
            setTimeout(() => this.reCheckEmptyRepo(), 5000);
          }
        },
        () => {}
      );
  }

  private indexInArray(idx: number, list: any[]): boolean {
    return idx > -1 && idx < list.length;
  }

  private swap(from: number, to: number, collection: any[]): any[] {
    const ret = [...collection];
    if (this.indexInArray(from, ret) && this.indexInArray(to, ret)) {
      [ret[from], ret[to]] = [ret[to], ret[from]];
    }
    return ret;
  }

  private getChartData(): void {
    const unixName = this.stateService.params.unixName;
    this.scmService.summary(unixName, { rel: 'chart_data,model', by_user: true })
      .then(
        (scmResponse) => {
          scmResponse.hostName = scmResponse.hostName.toString().replace(/"/g, '');
          this.scmData = scmResponse;
          this.commitChartData = this.scmChartService.getCommitChartData(scmResponse);
          this.scmChartOptions = this.scmChartService.getCommitChartOptions(this.commitChartData);

          if (this.nvd3 && this.nvd3.chart) {
            setTimeout(() => {
              this.nvd3.chart.update();
            });
          }

          if (!this.promotionModel || this.promotionModel.length === 0) {
            this.promotionModel = _.reject(scmResponse.rel.model, _.isEmpty);
            this.originalModel = this.promotionModel;
          }
        },
        () => {
          this.uiService.message.showWarning(
            'There was a problem loading scm summary: ' + this.project.name,
            'Data Error'
          );
        }
      );
  }

  fileSelected(files: FileList): void {
    if (!files || files.length === 0) {
      return;
    }

    const file = files[0];
    this.svnUpload = {
      fileName: file.name,
      size: file.size,
      amountComplete: 0,
      amountTotal: file.size,
      percentComplete: 0,
      percentRemaining: 100
    };

    const options = {
      successCallback: this.uploadFileToScm.bind(this),
      progressCallback: this.progressCallback.bind(this)
    };

    this.uploadService.uploadFiles('scm', _.get(this, 'project.id', 0), files, options);
  }

  private progressCallback(
    section: string,
    refId: number,
    file: any,
    bytesUploaded: number,
    bytesRemaining: number,
    percentUploaded: number,
    percentRemaining: number
  ): void {
    this.svnUpload.amountComplete = bytesUploaded;
    this.svnUpload.amountTotal = bytesRemaining;
    this.svnUpload.percentComplete = percentUploaded;
    this.svnUpload.percentRemaining = percentRemaining;
  }

  private uploadFileToScm(section: string, refId: number, file: any, hash: string): void {
    this.forceReload = true;

    this.http.post(`/api/scm/${refId}/repo/${hash}`, {}).subscribe(
      () => {
        this.uiService.message.showSuccess(
          'The dump file was properly uploaded, you will receive a notification when the import is finished'
        );
        this.svnUploadSuccess = true;
        setTimeout(() => this.reCheckEmptyRepo(), 1000);
      },
      () => {
        this.uiService.message.showError('Failed to upload the svn dump');
      },
      () => {
        this.svnUpload = false;
      }
    );
  }
}
